/**
 * DiapersPage — Module Couches SuiviPlus
 * Tableau de bord analytique : consommation, coûts, stock couches
 * Zéro saisie — agrège Orders, StockMovements, Crèche
 * Tabs : Tableau de bord | Détails mensuels | Configuration
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, Package, TrendingUp, TrendingDown,
  Baby, Calendar, Settings, BarChart3, AlertTriangle, Edit2, Save,
  Trash2, Info, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import {
  getDiapersDashboard, getDiapersComparison, getDiapersProducts,
  updateDiapersUnitsPerPackage, getDiapersConfig, updateDiapersConfig,
  getDiapersHistory, deleteDiapersHistoryBatch, getDiapersStructures,
} from '../api';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useStructure } from '../contexts/StructureContext';
import { useToast } from '../contexts/NotificationContext';
import LoadingSpinner from '../components/LoadingSpinner';

// ─── Helpers ────────────────────────────────────────────────────────────────────

const fmt = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);

const MONTH_SHORT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const COLORS = ['#0d9488', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981', '#3b82f6', '#ec4899', '#f97316'];

// ─── Composant KPI ──────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, icon: Icon, color = 'text-primary', trend }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon size={16} className={`${color} opacity-60`} />}
      <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</span>
    </div>
    <div className="flex items-end gap-2">
      <span className="text-2xl font-bold text-gray-900">{value}</span>
      {trend !== undefined && trend !== null && (
        <span className={`flex items-center text-xs font-medium ${trend >= 0 ? 'text-red-500' : 'text-green-500'}`}>
          {trend >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    {sub && <span className="text-xs text-gray-400 mt-1">{sub}</span>}
  </div>
);

// ─── Page principale ────────────────────────────────────────────────────────────

const DiapersPage = ({ showAllStructures }) => {
  const { currentStructure, structures } = useStructure();
  const toast = useToast();

  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [structuresData, setStructuresData] = useState(null);
  const [year, setYear] = useState(null);

  // Config tab state
  const [products, setProducts] = useState([]);
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [editingUnits, setEditingUnits] = useState({});
  const [editingConfig, setEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState({});

  const structureId = showAllStructures ? null : currentStructure?.id;

  // ─── Chargement données ───────────────────────────────────────────────────────

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (structureId) params.structureId = structureId;
      if (year) params.year = year;

      const { data } = await getDiapersDashboard(params);
      setDashboard(data);
      if (!year) setYear(data.year);

      // Charger comparaison N vs N-1
      const compParams = { ...params };
      if (data.year) {
        compParams.year1 = data.year - 1;
        compParams.year2 = data.year;
      }
      const { data: comp } = await getDiapersComparison(compParams);
      setComparison(comp);

      // Charger données par structure si vue "Toutes"
      if (showAllStructures) {
        const { data: strData } = await getDiapersStructures(params);
        setStructuresData(strData);
      }
    } catch (error) {
      console.error('Erreur chargement dashboard couches:', error);
      toast.error('Erreur chargement des données couches');
    } finally {
      setLoading(false);
    }
  }, [structureId, year, showAllStructures, toast]);

  const loadConfig = useCallback(async () => {
    try {
      // Toujours charger tous les produits (cross-structure) pour la config
      const [prodRes, confRes, histRes] = await Promise.all([
        getDiapersProducts({}),
        getDiapersConfig(),
        getDiapersHistory(),
      ]);
      setProducts(prodRes.data);
      setConfig(confRes.data);
      setConfigForm(confRes.data);
      setHistory(histRes.data);
    } catch (error) {
      console.error('Erreur chargement config couches:', error);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { if (tab === 'config') loadConfig(); }, [tab, loadConfig]);

  // ─── Handlers ─────────────────────────────────────────────────────────────────

  const handleSaveUnits = async (productId) => {
    try {
      await updateDiapersUnitsPerPackage(productId, { unitsPerPackage: editingUnits[productId] });
      toast.success('Unités par paquet mises à jour');
      setEditingUnits(prev => { const n = { ...prev }; delete n[productId]; return n; });
      loadConfig();
      loadDashboard();
    } catch (error) {
      toast.error('Erreur mise à jour');
    }
  };

  const handleSaveConfig = async () => {
    try {
      await updateDiapersConfig(configForm);
      toast.success('Configuration mise à jour');
      setEditingConfig(false);
      loadConfig();
      loadDashboard();
    } catch (error) {
      toast.error('Erreur mise à jour configuration');
    }
  };

  const handleDeleteBatch = async (batch) => {
    if (!window.confirm('Supprimer cet import historique ?')) return;
    try {
      await deleteDiapersHistoryBatch(batch);
      toast.success('Import supprimé');
      loadConfig();
      loadDashboard();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  const navigateYear = (delta) => {
    setYear(prev => (prev || new Date().getFullYear()) + delta);
  };

  // ─── Tabs ─────────────────────────────────────────────────────────────────────

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: BarChart3 },
    { id: 'details', label: 'Détails mensuels', icon: Calendar },
    { id: 'config', label: 'Configuration', icon: Settings },
  ];

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" text="Chargement..." />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Package size={24} className="text-primary" />
          Suivi des couches
        </h1>

        {/* Sélecteur année scolaire */}
        <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm border px-3 py-1.5">
          <button onClick={() => navigateYear(-1)} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm font-semibold text-gray-700 min-w-[80px] text-center">
            {dashboard?.schoolYear || '...'}
          </span>
          <button onClick={() => navigateYear(1)} className="p-1 hover:bg-gray-100 rounded">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.id
                ? 'bg-white text-primary shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <t.icon size={16} />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Contenu */}
      {tab === 'dashboard' && <DashboardTab dashboard={dashboard} comparison={comparison} structuresData={structuresData} showAllStructures={showAllStructures} loading={loading} />}
      {tab === 'details' && <DetailsTab dashboard={dashboard} />}
      {tab === 'config' && (
        <ConfigTab
          products={products}
          config={config}
          configForm={configForm}
          setConfigForm={setConfigForm}
          editingConfig={editingConfig}
          setEditingConfig={setEditingConfig}
          editingUnits={editingUnits}
          setEditingUnits={setEditingUnits}
          handleSaveUnits={handleSaveUnits}
          handleSaveConfig={handleSaveConfig}
          history={history}
          handleDeleteBatch={handleDeleteBatch}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 1 — TABLEAU DE BORD
// ═══════════════════════════════════════════════════════════════════════════════

const DashboardTab = ({ dashboard, comparison, structuresData, showAllStructures, loading }) => {
  if (!dashboard) return null;
  const { kpis, monthlyData, bySize } = dashboard;

  // Données graphique consommation mensuelle
  const chartData = monthlyData.filter(m => m.hasData).map(m => ({
    name: m.label,
    paquets: m.packagesOrdered || m.packagesConsumed,
    cout: m.totalCost,
  }));

  // Données graphique par taille — noms courts
  const shortSizeName = (name) => name
    .replace(/^Sac de \d+ couches /, '')
    .replace(/^Couches-culottes T(\d) Brigitte \(\d+\/sac\)/, 'Cul. T$1')
    .replace(/^Couches-culottes /, 'Cul. ');
  const sizeData = bySize.map((s, i) => ({
    name: shortSizeName(s.name),
    paquets: s.packages,
    fill: COLORS[i % COLORS.length],
  }));

  // Delta vs année précédente
  const costTrend = comparison?.delta?.cost;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard
          label="Coût total"
          value={fmt(kpis.totalCost)}
          icon={TrendingUp}
          color="text-red-500"
          trend={costTrend}
          sub={`${kpis.monthsWithData} mois`}
        />
        <KpiCard
          label="Paquets commandés"
          value={kpis.totalPackagesOrdered}
          icon={Package}
          color="text-indigo-500"
        />
        <KpiCard
          label="Moy. paquets/mois"
          value={kpis.avgMonthlyPackages}
          icon={Calendar}
          color="text-amber-500"
        />
        <KpiCard
          label="Couches/mois"
          value={(() => {
            const withData = monthlyData.filter(m => m.hasData && m.totalUnits > 0);
            if (withData.length === 0) return '—';
            return Math.round(withData.reduce((s, m) => s + m.totalUnits, 0) / withData.length);
          })()}
          icon={Baby}
          color="text-purple-500"
          sub="couches individuelles"
        />
        <KpiCard
          label="Stock actuel"
          value={`${kpis.currentStock} paq.`}
          icon={Package}
          color="text-green-500"
          sub={kpis.monthsBeforeStockout ? `~${kpis.monthsBeforeStockout} mois` : undefined}
        />
        <KpiCard
          label="Couches/enfant/jour"
          value={(() => {
            const withData = monthlyData.filter(m => m.hasData && m.unitsPerChildPerDay > 0);
            if (withData.length === 0) return '—';
            const avg = withData.reduce((s, m) => s + m.unitsPerChildPerDay, 0) / withData.length;
            return avg.toFixed(1);
          })()}
          icon={Baby}
          color="text-teal-500"
          sub={`base ${dashboard.config.defaultChildrenCount} enfants`}
        />
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Consommation mensuelle */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Commandes mensuelles</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, name) => [name === 'cout' ? fmt(v) : v, name === 'cout' ? 'Coût' : 'Paquets']} />
                <Area type="monotone" dataKey="paquets" stroke="#6366f1" fill="#6366f1" fillOpacity={0.15} name="Paquets" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">
              Aucune donnée pour cette période
            </div>
          )}
        </div>

        {/* Répartition par taille */}
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Répartition par taille</h3>
          {sizeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={sizeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" width={60} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [v, 'Paquets']} />
                <Bar dataKey="paquets" radius={[0, 4, 4, 0]}>
                  {sizeData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-gray-400 text-sm">
              Aucune donnée
            </div>
          )}
        </div>
      </div>

      {/* Comparaison année N vs N-1 */}
      {comparison && comparison.year1.monthsWithData > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Comparaison année scolaire</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="text-xs text-gray-400">{comparison.year1.label}</span>
              <div className="text-lg font-bold text-gray-700">{comparison.year1.totalPackages} paq.</div>
              <div className="text-sm text-gray-500">{fmt(comparison.year1.totalCost)}</div>
            </div>
            <div>
              <span className="text-xs text-gray-400">{comparison.year2.label}</span>
              <div className="text-lg font-bold text-gray-900">{comparison.year2.totalPackages} paq.</div>
              <div className="text-sm text-gray-500">{fmt(comparison.year2.totalCost)}</div>
            </div>
            <div>
              <span className="text-xs text-gray-400">Delta paquets</span>
              <div className={`text-lg font-bold flex items-center gap-1 ${comparison.delta.packages > 0 ? 'text-red-500' : 'text-green-500'}`}>
                {comparison.delta.packages !== null ? (
                  <>
                    {comparison.delta.packages > 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                    {Math.abs(comparison.delta.packages)}%
                  </>
                ) : '—'}
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-400">Delta coût</span>
              <div className={`text-lg font-bold flex items-center gap-1 ${(comparison.delta.cost || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
                {comparison.delta.cost !== null ? (
                  <>
                    {comparison.delta.cost > 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                    {Math.abs(comparison.delta.cost)}%
                  </>
                ) : '—'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Répartition par structure */}
      {showAllStructures && structuresData?.structures && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Répartition par structure</h3>
          <p className="text-xs text-gray-400 mb-3">
            Les données historiques (import) sont communes aux 2 structures. Les KPIs ci-dessus affichent le total réel sans doublon.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {structuresData.structures.map(s => (
              <div key={s.structureId} className="bg-gray-50 rounded-lg p-4">
                <div className="font-semibold text-gray-800 mb-2">{s.structureName}</div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-gray-400">Paquets</span>
                    <div className="font-bold text-gray-700">{s.totalPackages}</div>
                  </div>
                  <div>
                    <span className="text-gray-400">Coût</span>
                    <div className="font-bold text-gray-700">{fmt(s.totalCost)}</div>
                  </div>
                  <div>
                    <span className="text-gray-400">Stock</span>
                    <div className="font-bold text-gray-700">{s.currentStock} paq.</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 2 — DÉTAILS MENSUELS
// ═══════════════════════════════════════════════════════════════════════════════

const DetailsTab = ({ dashboard }) => {
  if (!dashboard) return null;
  const { monthlyData } = dashboard;

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 font-semibold text-gray-600">Mois</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600">Paq. commandés</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600">Paq. consommés</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600">Coût</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600">Enfants</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600">Paq./enfant</th>
              <th className="text-right px-4 py-3 font-semibold text-gray-600">Couches/enfant/j</th>
              <th className="text-center px-4 py-3 font-semibold text-gray-600">Source</th>
            </tr>
          </thead>
          <tbody>
            {monthlyData.map((m, i) => (
              <tr
                key={i}
                className={`border-b last:border-b-0 ${m.hasData ? 'hover:bg-gray-50' : 'text-gray-300'}`}
              >
                <td className="px-4 py-2.5 font-medium text-gray-700">
                  {MONTH_SHORT[m.month - 1]} {m.year}
                </td>
                <td className="px-4 py-2.5 text-right font-mono">
                  {m.hasData ? m.packagesOrdered : '—'}
                </td>
                <td className="px-4 py-2.5 text-right font-mono">
                  {m.packagesConsumed > 0 ? m.packagesConsumed : '—'}
                </td>
                <td className="px-4 py-2.5 text-right font-mono">
                  {m.hasData ? fmt(m.totalCost) : '—'}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {m.hasData ? m.childrenCount : '—'}
                </td>
                <td className="px-4 py-2.5 text-right font-mono">
                  {m.hasData ? m.packagesPerChildPerMonth : '—'}
                </td>
                <td className="px-4 py-2.5 text-right font-mono">
                  {m.hasData ? m.unitsPerChildPerDay : '—'}
                </td>
                <td className="px-4 py-2.5 text-center">
                  {m.historical && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-600 border border-amber-200">
                      Import
                    </span>
                  )}
                  {m.hasData && !m.historical && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-600 border border-green-200">
                      App
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {/* Totaux */}
          <tfoot>
            <tr className="bg-gray-50 font-semibold">
              <td className="px-4 py-3 text-gray-700">Total</td>
              <td className="px-4 py-3 text-right font-mono">{dashboard.kpis.totalPackagesOrdered}</td>
              <td className="px-4 py-3 text-right font-mono">{dashboard.kpis.totalPackagesConsumed || '—'}</td>
              <td className="px-4 py-3 text-right font-mono">{fmt(dashboard.kpis.totalCost)}</td>
              <td className="px-4 py-3 text-right">—</td>
              <td className="px-4 py-3 text-right">—</td>
              <td className="px-4 py-3 text-right">—</td>
              <td className="px-4 py-3"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TAB 3 — CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════════

const ConfigTab = ({
  products, config, configForm, setConfigForm, editingConfig, setEditingConfig,
  editingUnits, setEditingUnits, handleSaveUnits, handleSaveConfig,
  history, handleDeleteBatch,
}) => {
  // Grouper historique par batch
  const batches = {};
  history.forEach(h => {
    const key = h.importBatch || 'default';
    if (!batches[key]) batches[key] = { batch: key, rows: [], date: h.createdAt };
    batches[key].rows.push(h);
  });

  return (
    <div className="space-y-6">
      {/* Produits couches — unitsPerPackage (groupés par nom, cross-structure) */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Package size={16} className="text-primary" />
          Produits couches — Unités par paquet
        </h3>
        <p className="text-xs text-gray-400 mb-3">
          Indiquez le nombre de couches individuelles dans chaque paquet. La valeur est partagée entre toutes les structures.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2 font-semibold text-gray-600">Produit</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-600">Structures</th>
                <th className="text-right px-3 py-2 font-semibold text-gray-600">Stock total</th>
                <th className="text-right px-3 py-2 font-semibold text-gray-600">Couches/paquet</th>
                <th className="text-center px-3 py-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-gray-400">
                    <AlertTriangle size={20} className="inline mr-2 text-amber-400" />
                    Aucun produit trouvé avec la catégorie "{config?.categoryKeyword || 'couche'}"
                  </td>
                </tr>
              )}
              {(() => {
                // Grouper les produits par nom (même produit dans plusieurs structures)
                const grouped = {};
                products.forEach(p => {
                  if (!grouped[p.name]) grouped[p.name] = { ...p, structures: [], totalStock: 0 };
                  grouped[p.name].structures.push(p.structureName);
                  grouped[p.name].totalStock += p.currentStock;
                });
                return Object.values(grouped).map(g => {
                  const isEditing = editingUnits[g.id] !== undefined;
                  return (
                    <tr key={g.id} className="border-b last:border-b-0 hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-700">{g.name}</td>
                      <td className="px-3 py-2 text-gray-500">{g.structures.join(', ')}</td>
                      <td className="px-3 py-2 text-right font-mono">{g.totalStock}</td>
                      <td className="px-3 py-2 text-right">
                        {isEditing ? (
                          <input
                            type="number"
                            min="1"
                            value={editingUnits[g.id]}
                            onChange={e => setEditingUnits(prev => ({ ...prev, [g.id]: e.target.value }))}
                            className="w-20 px-2 py-1 border rounded text-right text-sm"
                            autoFocus
                          />
                        ) : (
                          <span className={`font-mono ${g.unitsPerPackage ? 'text-gray-700' : 'text-gray-300 italic'}`}>
                            {g.unitsPerPackage || 'Non défini'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {isEditing ? (
                          <button
                            onClick={() => handleSaveUnits(g.id)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                          >
                            <Save size={14} />
                          </button>
                        ) : (
                          <button
                            onClick={() => setEditingUnits(prev => ({ ...prev, [g.id]: g.unitsPerPackage || '' }))}
                            className="p-1 text-gray-400 hover:text-primary hover:bg-primary/5 rounded"
                          >
                            <Edit2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paramètres généraux */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Settings size={16} className="text-primary" />
            Paramètres
          </h3>
          {!editingConfig ? (
            <button
              onClick={() => setEditingConfig(true)}
              className="text-xs px-3 py-1.5 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
            >
              Modifier
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => { setEditingConfig(false); setConfigForm(config); }}
                className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveConfig}
                className="text-xs px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary-dark"
              >
                Sauvegarder
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-gray-500 font-medium">Mot-clé catégorie</label>
            {editingConfig ? (
              <input
                type="text"
                value={configForm.categoryKeyword || ''}
                onChange={e => setConfigForm(prev => ({ ...prev, categoryKeyword: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm mt-1"
              />
            ) : (
              <div className="text-sm font-mono text-gray-700 mt-1">{config?.categoryKeyword}</div>
            )}
            <span className="text-xs text-gray-400">Les produits dont la catégorie contient ce mot sont considérés comme des couches</span>
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium">Nombre d'enfants par défaut</label>
            {editingConfig ? (
              <input
                type="number"
                min="1"
                value={configForm.defaultChildrenCount || ''}
                onChange={e => setConfigForm(prev => ({ ...prev, defaultChildrenCount: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm mt-1"
              />
            ) : (
              <div className="text-sm font-mono text-gray-700 mt-1">{config?.defaultChildrenCount}</div>
            )}
            <span className="text-xs text-gray-400">Utilisé quand les données crèche ne sont pas disponibles</span>
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium">Jours ouvrés par mois</label>
            {editingConfig ? (
              <input
                type="number"
                min="1"
                value={configForm.workingDaysPerMonth || ''}
                onChange={e => setConfigForm(prev => ({ ...prev, workingDaysPerMonth: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg text-sm mt-1"
              />
            ) : (
              <div className="text-sm font-mono text-gray-700 mt-1">{config?.workingDaysPerMonth}</div>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium">Mois début année scolaire</label>
            {editingConfig ? (
              <select
                value={configForm.schoolYearStartMonth || 9}
                onChange={e => setConfigForm(prev => ({ ...prev, schoolYearStartMonth: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border rounded-lg text-sm mt-1"
              >
                {MONTH_SHORT.map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
            ) : (
              <div className="text-sm font-mono text-gray-700 mt-1">{MONTH_SHORT[(config?.schoolYearStartMonth || 9) - 1]}</div>
            )}
          </div>
        </div>
      </div>

      {/* Historique imports */}
      {Object.keys(batches).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Info size={16} className="text-amber-500" />
            Imports historiques
          </h3>
          <div className="space-y-2">
            {Object.values(batches).map(b => (
              <div key={b.batch} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                <div>
                  <span className="text-sm font-medium text-amber-700">
                    Import du {new Date(b.date).toLocaleDateString('fr-FR')}
                  </span>
                  <span className="text-xs text-amber-500 ml-2">
                    {b.rows.length} lignes
                  </span>
                </div>
                <button
                  onClick={() => handleDeleteBatch(b.batch)}
                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DiapersPage;
